import * as React from 'react';
import styled from 'styled-components';
import InputGroup from '../inputGroup/InputGroup';

// ############################ End Styles ###########################
// tslint:disable:variable-name

const SInputGroupWrapInner = styled.div<{ error: boolean; disabled: boolean }>(({ theme, error, disabled }) => {
  const borderColor = error ? theme.colors.error : theme.colors.border;
  const disabledStyle = disabled ? { opacity: 0.5 } : {};

  return {
    width: 'calc(100% - 2px)',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.grayWhite,
    borderRadius: theme.borderRadiusCommon,
    color: theme.colors.primaryBackground,
    position: 'relative',
    '&:focus-within': {
      borderColor: theme.colors.light,
    },
    input: {
      color: theme.colors.primaryBackground,
    },
    '&::placeholder': {
      color: theme.colors.placeholderGray,
    },
    ...disabledStyle,
  };
});

const SInputWrap = styled.div(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  '&:not(:first-of-type)': {
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      height: 'calc(100% - 20px)',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 1,
      backgroundColor: theme.colors.border,
    },
  },
}));

const SInput = styled.input(({ theme }) => ({
  textAlign: 'center',
  fontSize: theme.font.size.smallMedium2,
  boxSizing: 'border-box',
  color: theme.colors.primaryBackground,
  backgroundColor: 'transparent',
  borderWidth: 0,
  appearance: 'none',
  outline: 'none',
  padding: `${theme.spacing.medium}px 0px`,
  width: '100%',
  maxWidth: '100%',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 0,
  '&:invalid': {
    boxShadow: 'none',
  },
  '&::placeholder': {
    color: theme.colors.placeholderGray,
    fontSize: theme.font.size.smallMedium,
  },
}));

const SErrorMessage = styled.span(({ theme }) => ({
  fontWeight: theme.font.weight.light,
  position: 'absolute',
  fontSize: theme.font.size.smaller,
  left: '25px',
  bottom: '5px',
  color: theme.colors.error,
  WebkitTextFillColor: theme.colors.error,
}));

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface ICreditCardExpirationPartProps {
  name: string;
  dataAt: string;
  dataTestId?: string;
  value: string;
  placeholder: string;
  expirationRef?: React.RefObject<HTMLInputElement>;
  next?: React.RefObject<HTMLInputElement>;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyUp: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// tslint:disable-next-line:variable-name
const CreditCardExpirationPart: React.FC<ICreditCardExpirationPartProps> = (props) => {
  const { dataAt, dataTestId, ...inputProps } = props;

  return (
    <SInputWrap>
      <SInput
        maxLength={2}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        data-testid={dataTestId}
        {...inputProps}
        data-at={dataAt}
        ref={props.expirationRef}
      />
    </SInputWrap>
  );
};

export interface ICreditCardExpirationProps {
  onFilled?: (verificationCode: string, name: string, e?: HTMLInputElement) => void;
  value: string;
  className?: string;
  expirationRef?: React.RefObject<HTMLInputElement>;
  expirationMonthRef?: React.RefObject<HTMLInputElement>;
  expirationYearRef?: React.RefObject<HTMLInputElement>;
  next?: React.RefObject<HTMLInputElement>;
}

type creditCardExpirationState = {
  expirationMonth: string;
  expirationYear: string;
  error: string;
};

export class CreditCardExpiration extends React.Component<ICreditCardExpirationProps, creditCardExpirationState> {
  public static defaultProps = {
    value: '',
    className: '',
  };

  public state = {
    expirationMonth: '',
    expirationYear: '',
    error: '',
  };

  public componentDidMount() {
    this.validateExpiration();
  }

  private validateExpiration() {
    const { expirationMonth, expirationYear } = this.state;
    const expirationString = `${expirationYear}/${expirationMonth}`;

    const date = new Date();
    const currentDateString = `${date.getFullYear().toString().substr(-2)}/${`0${date.getMonth() + 1}`.substr(-2)}`;

    if (parseInt(expirationMonth, 10) > 12) {
      this.setState(() => ({ error: 'Invalid Month' }));
      return false;
    }
    if (expirationMonth && expirationYear && expirationString < currentDateString) {
      this.setState(() => ({ error: 'Card Expired' }));
      return false;
    }
    this.setState(() => ({ error: '' }));
    return true;
  }

  public handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // this.setState({ ...this.state, [event.currentTarget.name]: '' });
    // if (this.props.onFilled) {
    //   this.props.onFilled('', 'creditCardExpiration', event.target);
    // }
  };

  public handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const el = event.currentTarget;
    const aNumber = /^\d+$/.test(el.value);
    if (!aNumber) {
      const newValue = el.value.length <= 1 ? '' : el.value.slice(0, -1);
      this.setState({ ...this.state, [el.name]: newValue });
    }
    const val = this.state.expirationMonth + this.state.expirationYear;
    if (val.length === 4 && /^\d+$/.test(val)) {
      if (this.validateExpiration() && this.props.onFilled) {
        this.props?.next?.current.focus();
        this.props.onFilled(val, 'creditCardExpiration', el);
      }
    } else if (
      el.value.length === 2 &&
      aNumber &&
      el.parentElement &&
      el.parentElement.nextElementSibling &&
      el.parentElement.nextElementSibling.firstChild &&
      (el.parentElement.nextElementSibling.firstChild as HTMLInputElement).value.length < 2
    ) {
      (el.parentElement.nextElementSibling.firstChild as HTMLElement).focus();
    }
  };

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valueCandidate = event.currentTarget.value;
    if (valueCandidate.length <= 2) {
      this.setState({ ...this.state, [event.currentTarget.name]: valueCandidate });
    }
  };

  public static getDerivedStateFromProps(nextProps: ICreditCardExpirationProps, prevState: creditCardExpirationState) {
    if (prevState.expirationMonth === '' && prevState.expirationYear === '' && nextProps.value.length === 4) {
      return {
        expirationMonth: nextProps.value.substring(0, 2),
        expirationYear: nextProps.value.substring(2, 4),
      };
    }
    return {};
  }

  public render() {
    const commonInputProps = {
      onFocus: this.handleFocus,
      onKeyUp: this.handleKeyUp,
      onChange: this.handleChange,
    };

    const { expirationMonthRef, expirationYearRef } = this.props;

    return (
      <InputGroup className={this.props.className} dataAt="creditCardExpiration">
        <SInputGroupWrapInner error={Boolean(this.state.error)} disabled={false}>
          <CreditCardExpirationPart
            {...commonInputProps}
            placeholder="MM"
            name="expirationMonth"
            dataAt="creditCardExpMonth"
            dataTestId="card-expiration-mm"
            value={this.state.expirationMonth}
            expirationRef={expirationMonthRef}
            next={this.props.expirationRef}
          />
          <CreditCardExpirationPart
            {...commonInputProps}
            expirationRef={expirationYearRef}
            placeholder="YY"
            name="expirationYear"
            dataAt="creditCardExpYear"
            dataTestId="card-expiration-yy"
            value={this.state.expirationYear}
          />
        </SInputGroupWrapInner>
        <SErrorMessage>{this.state.error}</SErrorMessage>
      </InputGroup>
    );
  }
}
